<template>
  <div
    :class="[
      {
        'main': !id
      }
    ]"
  >
    <!-- Loading Spinner -->
    <module-spinner
      :active="loading"
      :floating="true"
    />

    <div
      :class="[
        {
          'group' : !id,
          'dashboard': !id,
          'dashboard--full-screen': !id,
        }
      ]"
    >
      <!-- Back Button -->
      <module-button
        class="mt0 mb"

        button="tertiary"
        icon="arrow-left"
        :link="request.state === 4 || isOwnProcedure ? `dashboard-requests` : `dashboard-reviews`"
        :negative="true"
        :label="!isReviewer ? `Back to requests` : status === 204 || request.state !== 3 || isOwnProcedure ? `Back to Dashboard` : `Stop Reviewing` "
      />
      <h1 v-show="!loading && status === 204" class="h3">There are currently no pending reviews</h1>
      <transition
        name="fade"
      >
        <div
          class="review"
          v-show="!loading && status === 200"
        >
          <!-- Heading -->
          <div class="review__heading">
            <h1 class="h3">
              <span v-if="isReviewer" class="fw-medium">Procedure Type: </span>{{ reviewNomenclatureBySlugAndId(`procedure`, request.procedure) || `Request` }}
              <span class="c-mid" v-if="!isReviewer && request.state <= 3">(Pending)</span>
            </h1>
            <template v-if="userRole.role === 1">
              <p class="p c-tertiary">Performed by <span class="fw-bold">Dr {{ doctor.full_name }} &lt;{{ doctor.email }}&gt;</span> <span v-if="procedureDate" v-html="procedureDate"/></p>
              <p class="p c-tertiary">{{ stateMessage[request.state] }}</p>
            </template>


            <hr>
          </div>
          <!-- Meta data -->
          <div class="review__procedure-info">
            <h5 class="h5">Procedure Details</h5>
            <module-detail-list
              :items="{
                'Procedure Date': request.most_recent_procedure ? `Most recent procedure` : request.date_of_procedure,
                'Procedure Details': request.procedure_type ? procedureType[request.procedure_type] : `(Pending)`,
                'Tooth Number': request.tooth_number ? request.tooth_number : `(Pending)`,
                'Rubber Dam Used': typeof request.was_rubber_dam_used === 'boolean' ? request.was_rubber_dam_used === false && 'No' || 'Yes' : `(Pending)`,
              }"
            />
            <hr>
          </div>

          <!-- X Rays -->
          <div
            :class="[
              `review__xrays`,
              {
                'review__xrays--reviewer': isReviewer,
              },
            ]"
          >
            <h2 class="h4" v-if="!isReviewer">Procedure X-Rays</h2>
            <ul class="review-previews">
              <template v-for="category in files">
                <li
                  v-for="file in category"
                  :key="file.id"

                  class="review-previews__item"
                >
                  <module-thumbnail
                    :image="(file.image || {}).thumbnail"

                    @click.native="openOverlay(file.image.original)"
                  />
                  <div>
                    <span class="h5 mb0 mt0 fwbold">{{ xrayTitle[file.category] }}</span>     <!-- {{ file.category || `X-ray #${index + 1}` }} -->
                    <span class="small mt0">Date: {{ file.date }}</span>
                  </div>
                </li>
              </template>
              <template v-if="!Object.keys(files).length">
                <p class="p">No X-Rays Provided</p>
                <p><small>Were you provided X-Rays? Click here to <module-link class="location__link fw-bold" :link="{name: `request-complete`, params: { id }}">Upload X-Rays</module-link></small></p>
              </template>
            </ul>

            <module-modal
              id="review-overlay"
            >
              <img
                v-if="overlay"

                class="review-overlay__image"

                :src="overlay"
              >
              <div
                v-if="!isReviewer"
                class="review-overlay__thumbnails"
              >
                <module-thumbnail
                  v-for="(file, index) in files"
                  :key="index"
                  :class="[
                    {
                      'is--active': overlay === (file.image || {}).original,
                    },
                  ]"
                  :image="(file.image || {}).thumbnail"
                  @click.native="overlay = file.image.original"
                />
              </div>
            </module-modal>
          </div>

          <!-- Review Results -->
          <div
            v-if="review.id"

            class="review__results"
          >
            <h2 class="h2">{{ review.rating }}/10</h2>
            <div v-for="(questionContent, slug) in reviewQuestions" :key="slug" :class="`expand-${slug}`">
              <p class="p"><ExpandButton v-if="isPatient" :class="`expand-${slug}`" />&nbsp;<strong>{{ questionContent.heading }}</strong></p>
              <p class="p mb"><i>{{ reviewAnswers[review[slug]] }}</i></p>
              <div v-if="isPatient" class="clearfix" />
              <Expander v-if="isPatient" :trigger-element="`.expand-${slug}`">
                <ui-tooltip
                  level="tip"
                  :close="false"
                >
                  <template slot="message">
                    {{ questionContent.tooltip }}
                  </template>
                </ui-tooltip>
              </Expander>
              <div v-if="isPatient" class="clearfix mb" style="padding-bottom: 10px;"/>
            </div>
            <hr>
            <p class="p" v-if="isPatient"><strong>Reviewed</strong><template v-if="reviewer.full_name"> by <strong>a VeriTooth specialist</strong></template> on <strong>{{ formatDate(review.created_at) }}</strong></p>
            <p class="p" v-else><strong>Reviewed</strong><template v-if="reviewer.full_name"> by <strong>{{ reviewer.full_name }}</strong></template> on <strong>{{ formatDate(review.created_at) }}</strong></p>
            <p class="p" v-if="review.reviewer_notes && !isPatient">{{ review.reviewer_notes }}</p>
            <module-button
              v-if="successButton[userRole.role]"

              button="primary"

              v-bind="successButton[userRole.role]"
              @click.native="nextReview()"
            />
          </div>

          <!-- Review Form -->
          <div
            v-else-if="isReviewer && !isOwnProcedure"

            class="review__form"
          >
            <h2 class="h4">Your Review</h2>
            <module-form
              v-if="!hideForm"
              form-id="submit-review"
              :fields="fields.reviewer"
              :wide ="true"
              :action="action"
              :success="success"
              :fail="fail"
              button="primary"
            >
              <module-button
                slot="submit-extras"
                button="lightest"
                size="m"
                type="button"
                label="Skip This Review"
                @click.native="nextReview()"
              />
            </module-form>
            <p class="small submit-details">When you submit this review, it will be automatically sent to the patient and their doctor</p>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

// Services
import { toDateString } from 'services/dateTime';
import { arrayLoop } from 'services/array';
// Models
import { ratingsMap, fields } from 'models/review';

// Modules
import ModuleButton from 'modules/Button';
import ModuleForm from 'modules/Form';
import ModuleLink from 'modules/Link';
import ModuleSpinner from 'modules/Spinner';
import ModuleDetailList from 'modules/DetailList';
import ModuleModal from 'modules/Modal';
import ModuleThumbnail from 'modules/Thumbnail';

// UI
import ExpandButton from 'ui/ExpandButton';
import Expander from 'ui/Expander';
import UiTooltip from 'ui/Tooltip';

// Export
export default {
  name: `review`,

  // Components
  components: {
    ModuleButton,
    ModuleForm,
    ModuleLink,
    ModuleSpinner,
    ModuleDetailList,
    ModuleModal,
    ModuleThumbnail,
    ExpandButton,
    Expander,
    UiTooltip,
  },

  // Data
  data() {
    return {
      loading: true,
      request: {},
      status: ``,
      overlay: ``,
      fields,
      hideForm: false,

    };
  },
  computed: {
    ...mapGetters(`auth`, [
      `user`,
      `userRole`,
      `userIsRole`,
    ]),
    ...mapGetters(`forms`, [
      `getModel`,
    ]),
    ...mapGetters(`review`, [
      `reviewNomenclatureBySlugAndId`,
    ]),
    model() {
      return this.getModel(`submit-review`);
    },
    id() {
      return this.$route.params.id;
    },
    procedure() {
      return this.request.procedure || {};
    },
    reason() {
      return this.request.reason || {};
    },
    diagnosis() {
      return this.request.diagnosis || {};
    },
    review() {
      const review = this.request.review || {};

      review.ratingMapped = ratingsMap[review.rating];

      return review;
    },
    reviewer() {
      const reviewer = this.review.reviewer || {};

      const firstName = reviewer.first_name || ``;
      const lastName = reviewer.last_name || ``;
      const fullName = `${firstName}${firstName && lastName ? ` ` : ``}${lastName}`;

      reviewer.full_name = fullName || ``;

      return reviewer;
    },
    doctor() {
      const doctor = this.request.doctor || {};

      const firstName = doctor.first_name || ``;
      const lastName = doctor.last_name || ``;
      const fullName = `${firstName}${firstName && lastName ? ` ` : ``}${lastName}`;

      doctor.full_name = fullName || ``;

      return doctor;
    },
    procedureDate() {
      return this.request.most_recent_procedure ? false : `on <span class="fw-bold">${this.formatDate(this.request.date_of_procedure)}</span>`;
    },
    isReviewer() {
      return this.userRole.role === 3 || this.userRole.role === 42;
    },
    isOwnProcedure() {
      return this.request && this.request.doctor && this.request.doctor.id !== this.user.id;
    },
    isPatient() {
      return this.userRole.role === 1;
    },
    reviewQuestions() {
      return {
        was_rubber_dam_used: {
          heading: `Rubber Dam Used`,
          tooltip: `A Rubber Dam is considered the standard of care and should be used in all instances. \
                    It may be awkward, but this protects the area the doctor is working on and greatly reduces \
                    the potential for future infection.`,
        },
        conservative_access_prep: {
          heading: `Conservative Access Prep`,
          tooltip: `Has the dentist drilled the small hole needed, or a much bigger hole than is typically required? \
                    A bigger hole not only damages your tooth more, it also increases your risk of future problems.`,
        },
        all_canal_cleaned_and_shaped: {
          heading: `All Canals Cleaned and Shaped`,
          tooltip: `Did you know there is not a standard number of canals for each tooth? So finding \
                    all the canals can be a challenge.  Did the dentist treat each of the canals of your \
                    tooth or did he/she miss one. Missing a canal can leave an opening for future infections \
                    and require another procedure in the future.`,
        },
        canal_cleaned_and_shaped: {
          heading: `Canals Cleaned and Shaped to the Apex`,
          tooltip: `The filling should go to the tip of your root. Not filling enough (under-fill) \
                    can leave space for harmful bacteria to grow. A filling that goes beyond the root \
                    tip (over-fill) can cause damage to the bone, sinus, or even nerve damage in severe cases. \
                    Over or under filling increases the chances that you'll need to have another procedure down \
                    the road.`,
        },
        adequate_root_density: {
          heading: `Adequate root filling density`,
          tooltip: `The canals should be filled without leaving any spaces between the walls of the tooth. \
                    Spaces can leave room for bacteria and toxins that eventually cause an infection, often \
                    requiring another root canal.`,
        },
        perforation: {
          heading: `Signs of perforation`,
          tooltip: `Dentists are human and sometimes make mistakes. We’re simply looking to see if the dentist \
                    drilled out the side of your tooth. Yep, it happens.`,
        },
        separated_instrument: {
          heading: `Signs of separated instrument`,
          tooltip: `Sometimes a dentist can break off a piece of the small drill bit or file in your tooth. \
                    These tools are very delicate and must be used with great care. Ideally the dentist will \
                    get the broken piece out.  If it’s not possible to get it out, did the dentist at least \
                    tell you about this?`,
        },
      };
    },
    reviewAnswers() {
      return {
        1: `Yes`,
        2: `No`,
        3: `Unable to tell`,
      };
    },
    stateMessage() {
      return {
        2: `This request is still pending X-Ray uploads from your dentist, we'll notify you when it's complete`,
        3: `This request is still pending review, we'll notify you when it's complete`,
        4: `This request has been completed, please see review below`,
      };
    },
    procedureType() {
      return {
        1: `Restorative`,
        2: `Fixed Pros`,
        3: `Endodontics`,
      };
    },
    successButton() {
      return {
        1: {
          label: `Request another review`,
          link: `request`,
        },
        3: {
          label: `Review another request`,
        },
      };
    },
    xrayTitle() {
      return {
        1: `Pre-op`,
        2: `Post-op`,
        3: `Working Film`,
      };
    },
    files() {
      const files = this.request.files || [];
      const sorted = {};

      if (!files.length) return sorted;

      arrayLoop(files, (index, file) => {
        const category = file.category;
        if (!sorted[category]) {
          sorted[category] = [
            file,
          ];
        }
        else {
          sorted[category].push(file);
        }
      });

      return sorted;
    },

  },

  // Methods
  methods: {
    ...mapActions(`modals`, [
      `openModal`,
    ]),
    ...mapActions(`review`, [
      `getReviewRequestById`,
      `getNextReview`,
      `submitReview`,
    ]),
    ...mapActions(`forms`, [
      `resetForm`,
    ]),
    action() {
      return this.submitReview({
        id: this.request.id,
        ...this.model,
      });
    },
    success(success) {
      this.$set(this.request, `review`, success);
    },
    fail(fail) {
      console.warn(fail);
    },
    openOverlay(file) {
      this.overlay = file;
      this.openModal(`review-overlay`);
    },

    formatDate(date) {
      return toDateString(date);
    },

    getReview() {
      return this.id ?  this.getReviewRequestById(this.id) : this.getNextReview();
    },

    nextReview() {
      if(this.isReviewer) {
        this.loading = true;
        setTimeout(() => {
          this.hideForm = true;
          this.getNextReview()
            .then(res => {
              this.status = res.status;
              this.$set(this, `request`, res.data || {});
              this.$nextTick(() => {
                this.hideForm = false;
                this.loading = false;
                this.scrollToTop();
              });
            });
        }, 200);
      }
    },

    scrollToTop() {
      window.scrollTo(0,0);
    },
  },

  // Lifecycle
  created() {
    this.getReview()
      .then(res => {
        this.loading = false;
        this.status = res.status;
        // If user role is patient this is their review
        if(this.userIsRole([1])) {
          // Check to see if payment request satisfied
          if(! res.data.order) {
            alert(`Can not validate payment. Cannot Continue`); // eslint-disable-line no-alert
            return this.$router.push({
              name: `dashboard`,
            });
          }
          if(! res.data.order || res.data.order.state !== 2) {
            return this.$router.replace({
              name: `review-payment-request`,
              params: { id: res.data.order.id, redirect_to: this.$route.fullPath },
            });
          }
        }
        this.$set(this, `request`, res.data || {});
      });
  },
};
</script>



// WEBPACK FOOTER //
// Review.vue?f174e37c